import * as React from 'react';
import { registerCurrencies } from '@/lib/money';
import { FALLBACK_BASE_CURRENCY } from '@/lib/config';
import { localizedName } from '@/lib/locale';
import { useAuth, AUTH_STATUS } from '@/features/auth/AuthContext';
import { useCurrencies } from './currency.queries';

/**
 * The live currency list, available to every screen.
 *
 * Two jobs, and the second is the one that matters.
 *
 * 1. It hands components the list, so a picker has something to offer and a label knows
 *    what to print.
 * 2. **It teaches `money.js` each currency's decimals.** Amounts are integers in minor
 *    units (I1), and how many minor units make a major one is a property of the currency
 *    — three for a dinar, two for a dollar, none for a yen. Before this, that table was a
 *    hard-coded literal, so a currency somebody created this morning parsed with two
 *    decimals whatever it said, and `12.345` in a form silently became `12.35`. Priming
 *    the register on every response is what makes "add a currency" a real feature rather
 *    than a row in a list.
 *
 * Deliberately non-blocking: children render while the query is in flight, against the
 * fallback base currency, because a spinner over the whole application while a five-row
 * lookup completes is a worse first impression than a currency code that settles.
 */

const CurrencyContext = React.createContext(null);

/** A currency the app can always name, so nothing downstream handles `undefined`. */
const fallbackCurrency = (code) => ({
  code,
  name: code,
  nameAr: null,
  nameCkb: null,
  symbol: null,
  decimals: 2,
  isBase: true,
  isActive: true,
  rateMicro: 1_000_000,
});

export const CurrencyProvider = ({ children }) => {
  /*
   * **Only ask once there is a session to ask with.**
   *
   * This used to fire on mount regardless — an anonymous caller got a 401, the query
   * failed quietly and the fallback carried the login screen. Quiet was the problem. That
   * 401 also went through the API client's session-lost path, which clears the *entire*
   * query cache, and a query cleared out from under a mounted observer is orphaned: nothing
   * — not a later invalidation, not a window focus — ever fetched it again. Signing in
   * navigates without a reload, so the registry stayed on the base currency alone, every
   * form hid its currency picker (one option is not a choice), and the office concluded
   * the dinar did not exist until somebody pressed F5.
   *
   * Gating on the session means the first request is one that can succeed, and flipping
   * `enabled` re-attaches the observer to the cache, so a login after a logout fetches
   * afresh as well. The provider already sat inside `AuthProvider` for exactly this
   * reason (`app/providers.jsx`); it simply was not using the fact.
   */
  const { status } = useAuth();
  const { data, isLoading, isError } = useCurrencies(
    {},
    { retry: false, enabled: status === AUTH_STATUS.AUTHENTICATED },
  );

  const value = React.useMemo(() => {
    const list = Array.isArray(data) && data.length ? data : [fallbackCurrency(FALLBACK_BASE_CURRENCY)];

    // Side effect in a memo is deliberate and safe: it is idempotent, and it must happen
    // before any child renders an amount, not in an effect that fires after the paint.
    registerCurrencies(list);

    const byCode = new Map(list.map((c) => [c.code, c]));
    const base = list.find((c) => c.isBase) ?? list[0];

    return {
      currencies: list,
      /** What may be *chosen*. Inactive currencies stay readable — old documents use them. */
      selectable: list.filter((c) => c.isActive),
      byCode,
      baseCurrency: base.code,
      base,
      isLoading,
      isError,
      get: (code) => byCode.get(String(code || '').toUpperCase()) ?? null,
    };
  }, [data, isLoading, isError]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

/**
 * @returns {{currencies: any[], selectable: any[], baseCurrency: string, get: (c: string) => any}}
 */
export const useCurrencyRegistry = () => {
  const context = React.useContext(CurrencyContext);
  if (!context) {
    // A component rendered outside the provider — a unit test, usually. Answering with the
    // fallback keeps it renderable instead of throwing on a currency code.
    const base = fallbackCurrency(FALLBACK_BASE_CURRENCY);
    return {
      currencies: [base],
      selectable: [base],
      byCode: new Map([[base.code, base]]),
      baseCurrency: base.code,
      base,
      isLoading: false,
      isError: false,
      get: (code) => (code === base.code ? base : null),
    };
  }
  return context;
};

/** The group's reporting currency — the default for any amount nobody has said otherwise about. */
export const useBaseCurrency = () => useCurrencyRegistry().baseCurrency;

/** A currency's name in the reader's language, falling back to English then the code. */
export const useCurrencyLabel = () => {
  const { get } = useCurrencyRegistry();
  return React.useCallback(
    (code, locale) => {
      const row = get(code);
      if (!row) return code;
      const name = localizedName(row, locale);
      return name && name !== row.code ? `${row.code} — ${name}` : row.code;
    },
    [get],
  );
};

export default CurrencyProvider;
